import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { CheckCircle, Copy, Download, Shield } from 'lucide-react';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'qr' | 'verify' | 'backup' | 'disable';

export function TwoFactorSetupModal({ isOpen, onClose }: TwoFactorSetupModalProps) {
  const { user, refreshAuthState } = useAuthStore();
  const isEnabled = user?.preferences?.twoFactorEnabled;

  const [step, setStep] = useState<Step>(isEnabled ? 'disable' : 'qr');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSetup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await authService.setupTwoFactor();
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setStep('qr');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.length !== 6) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await authService.verifyTwoFactor(code);
      setBackupCodes(result.backupCodes);
      setStep('backup');
      refreshAuthState();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !code) return;
    setIsLoading(true);
    setError(null);
    try {
      await authService.disableTwoFactor(password, code);
      refreshAuthState();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!code) return;
    setIsLoading(true);
    setError(null);
    try {
      const codes = await authService.regenerateBackupCodes(code);
      setBackupCodes(codes);
      setStep('backup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate codes');
    } finally {
      setIsLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'godmode-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStep(isEnabled ? 'disable' : 'qr');
    setQrCode('');
    setSecret('');
    setCode('');
    setPassword('');
    setBackupCodes([]);
    setError(null);
    setCopied(false);
    onClose();
  };

  // Auto-initiate setup when opening for non-enabled users
  useEffect(() => {
    if (isOpen && !isEnabled && !qrCode && step === 'qr') {
      handleSetup();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEnabled ? 'Manage Two-Factor Authentication' : 'Set Up Two-Factor Authentication'}
      size="sm"
    >
      {error && (
        <Alert variant="destructive" className="mb-4">{error}</Alert>
      )}

      {/* Step 1: QR Code */}
      {step === 'qr' && !isEnabled && (
        <div className="space-y-4">
          {qrCode ? (
            <>
              <p className="text-sm text-muted-foreground">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <div className="flex justify-center">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Or enter this key manually:</p>
                <code className="block text-xs bg-muted p-2 rounded font-mono break-all select-all">
                  {secret}
                </code>
              </div>
              <Button className="w-full" onClick={() => setStep('verify')}>
                Continue
              </Button>
            </>
          ) : (
            <div className="flex justify-center py-8">
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Verify Code */}
      {step === 'verify' && (
        <form onSubmit={handleVerify} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app to verify setup.
          </p>
          <div className="space-y-1.5">
            <label htmlFor="totp-code" className="text-sm font-medium">Verification Code</label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-center tracking-widest font-mono text-lg"
              placeholder="000000"
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setStep('qr')}>
              Back
            </Button>
            <Button type="submit" className="flex-1" disabled={code.length !== 6 || isLoading}>
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
        </form>
      )}

      {/* Step 3: Backup Codes */}
      {step === 'backup' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <p className="font-medium">Two-factor authentication enabled</p>
          </div>
          <p className="text-sm text-muted-foreground">
            Save these backup codes in a safe place. Each code can only be used once to sign in if you lose access to your authenticator app.
          </p>
          <div className="bg-muted p-3 rounded-md">
            <div className="grid grid-cols-2 gap-1">
              {backupCodes.map((c, i) => (
                <code key={i} className="text-sm font-mono">{c}</code>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={copyBackupCodes}>
              <Copy className="h-4 w-4 mr-1" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={downloadBackupCodes}>
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>
          <Button className="w-full" onClick={handleClose}>
            Done
          </Button>
        </div>
      )}

      {/* Manage: Disable or Regenerate */}
      {step === 'disable' && isEnabled && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <Shield className="h-5 w-5" />
            <p className="font-medium">Two-factor authentication is enabled</p>
          </div>

          <form onSubmit={handleDisable} className="space-y-3">
            <p className="text-sm font-medium">Disable 2FA</p>
            <div className="space-y-1.5">
              <label htmlFor="disable-password" className="text-sm text-muted-foreground">Password</label>
              <input
                id="disable-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="disable-code" className="text-sm text-muted-foreground">TOTP Code</label>
              <input
                id="disable-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-center tracking-widest font-mono"
                placeholder="000000"
                autoComplete="one-time-code"
              />
            </div>
            <Button type="submit" variant="destructive" className="w-full" disabled={!password || code.length !== 6 || isLoading}>
              {isLoading ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          </form>

          <hr />

          <div className="space-y-2">
            <p className="text-sm font-medium">Regenerate Backup Codes</p>
            <p className="text-xs text-muted-foreground">
              This will invalidate all existing backup codes.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleRegenerateBackupCodes}
              disabled={code.length !== 6 || isLoading}
            >
              Regenerate Codes
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
